import { sdkTypes } from '@vansite/ts-sharetribe-flex-sdk';

export const LABEL_HANDLE = 'SearchMapLabel';
export const INFO_CARD_HANDLE = 'SearchMapInfoCard';
export const SOURCE_AUTOCOMPLETE = 'autocomplete';

const { LatLng: SDKLatLng, LatLngBounds: SDKLatLngBounds } = sdkTypes;

/**
 * Fit part of map (descriped with bounds) to visible map-viewport
 * @param {object} map - map that needs to be centered with given bounds
 * @param {import('@vansite/ts-sharetribe-flex-sdk').LatLngBounds} bounds - the area that needs to be visible when map loads.
 * @param options
 */
export const fitMapToBounds = (map, bounds, options) => {
  const { padding = 0, isAutocompleteSearch = false } = options;

  // map bounds as string literal for google.maps
  const mapBounds = sdkBoundsToMapboxBounds(bounds);
  const paddingOptionMaybe = padding == null ? { padding } : {};
  const eventData = isAutocompleteSearch ? { searchSource: SOURCE_AUTOCOMPLETE } : {};

  // If bounds are given, use it (defaults to center & zoom).
  if (map && mapBounds) {
    map.fitBounds(mapBounds, { ...paddingOptionMaybe, linear: true, duration: 0 }, eventData);
  }
};

/**
 * Convert Mapbox formatted LatLng object to Sharetribe SDK's LatLng coordinate format
 * Longitudes > 180 and < -180 are converted to the correct corresponding value
 * between -180 and 180.
 * @param {mapboxgl.LngLat} mapboxLngLat - Mapbox LngLat
 * @param lngLat
 * @returns {import('@vansite/ts-sharetribe-flex-sdk').LatLng} - Converted latLng coordinate
 */
export const mapboxLngLatToSDKLatLng = (lngLat) => {
  const mapboxLng = lngLat.lng;

  // For bounding boxes that overlap the antimeridian Mapbox sometimes gives
  // longitude values outside -180 and 180 degrees.Those values are converted
  // so that longitude is always between -180 and 180.
  const lng = mapboxLng > 180 ? mapboxLng - 360 : mapboxLng < -180 ? mapboxLng + 360 : mapboxLng;

  return new SDKLatLng(lngLat.lat, lng);
};

/**
 * Convert Mapbox formatted bounds object to Sharetribe SDK's bounds format
 * @param {mapboxgl.LngLatBounds} mapboxBounds - Mapbox LngLatBounds
 * @returns {import('@vansite/ts-sharetribe-flex-sdk').LatLngBounds | null} - Converted bounds
 */
export const mapboxBoundsToSDKBounds = (mapboxBounds) => {
  if (!mapboxBounds) {
    return null;
  }

  const ne = mapboxBounds.getNorthEast();
  const sw = mapboxBounds.getSouthWest();
  return new SDKLatLngBounds(mapboxLngLatToSDKLatLng(ne), mapboxLngLatToSDKLatLng(sw));
};

/**
 * Convert sdk bounds that overlap the antimeridian into values that can
 * be passed to Mapbox. This is achieved by converting the SW longitude into
 * a value less than -180 that flows over the antimeridian.
 * @param {import('@vansite/ts-sharetribe-flex-sdk').LatLngBounds} bounds - bounds passed to the map
 * @returns {Array<Array<number>>} a bounding box that is compatible with Mapbox
 */
const sdkBoundsToMapboxBounds = (bounds) => {
  if (!bounds) {
    return null;
  }
  const { ne, sw } = bounds;

  // if sw lng is > ne lng => the bounds overlap antimeridian
  // => flip the nw lng to the negative side so that the value
  // is less than -180
  const swLng = sw.lng > ne.lng ? -360 + sw.lng : sw.lng;

  return [
    [swLng, sw.lat],
    [ne.lng, ne.lat],
  ];
};

/**
 * Return map bounds as SDKBounds
 * @param {mapboxgl} map - Mapbox map from where the bounds are asked
 * @returns {import('@vansite/ts-sharetribe-flex-sdk').LatLngBounds} - Converted bounds of given map
 */
export const getMapBounds = (map) => mapboxBoundsToSDKBounds(map.getBounds());

/**
 * Return map center as SDKLatLng
 * @param {mapboxgl} map - Mapbox map from where the center is asked
 * @returns {import('@vansite/ts-sharetribe-flex-sdk').LatLng} - Converted center of given map
 */
export const getMapCenter = (map) => mapboxLngLatToSDKLatLng(map.getCenter());

/**
 * Check if map library is loaded
 * @returns {boolean | undefined | string} - true if map library is loaded
 */
export const isMapsLibLoaded = () =>
  typeof window !== 'undefined' && window.mapboxgl && window.mapboxgl.accessToken;
